import React, { useCallback, useEffect, useRef, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Plus, FolderOpen, LogOut, ChevronRight, Database, Trash2,
  ThumbsUp, ThumbsDown, CheckCircle2, ShieldAlert, ArrowLeft, Zap,
  Mail, Lock, User, Building2, ExternalLink, KeyRound, Search,
  Edit2, Upload, FileText, UserCheck, Shield, Eye, EyeOff, MoreHorizontal
} from 'lucide-react'
import { Toaster, toast } from 'react-hot-toast'
import { useStore } from './lib/store'
import { apiGet, apiPost, apiPatch, apiDelete, apiUpload, bootstrapAuth, supabase, API_BASE } from './lib/api'
import ChatView from './components/chat/ChatView'
import { Button } from './components/ui/button'
import { Input } from './components/ui/input'
import { Textarea } from './components/ui/textarea'
import { Badge } from './components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './components/ui/dialog'
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from './components/ui/tooltip'
import UsageBadge from './components/UsageBadge'
import UsageDashboard from './components/UsageDashboard'
import './index.css'

// ─── Change Password Dialog ───────────────────────────────────────────────────

function ChangePasswordDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [form, setForm] = useState({ current: '', next: '', confirm: '' })
  const [loading, setLoading] = useState(false)

  const handleSubmit = async () => {
    if (form.next !== form.confirm) { toast.error('New passwords do not match'); return }
    if (form.next.length < 8) { toast.error('New password must be at least 8 characters'); return }
    setLoading(true)
    try {
      await apiPost('/api/auth/change-password', {
        current_password: form.current,
        new_password: form.next,
      })
      toast.success('Password updated successfully')
      setForm({ current: '', next: '', confirm: '' })
      onClose()
    } catch (e: any) {
      toast.error(e.message || 'Password change failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { setForm({ current: '', next: '', confirm: '' }); onClose() } }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change password</DialogTitle>
        </DialogHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ display: 'block', fontSize: 11.5, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>
              Current password
            </label>
            <Input type="password" value={form.current}
              onChange={e => setForm(f => ({ ...f, current: e.target.value }))}
              placeholder="••••••••" autoFocus />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11.5, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>
              New password
            </label>
            <Input type="password" value={form.next}
              onChange={e => setForm(f => ({ ...f, next: e.target.value }))}
              placeholder="••••••••" />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11.5, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>
              Confirm new password
            </label>
            <Input type="password" value={form.confirm}
              onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
              placeholder="••••••••"
              onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading || !form.current || !form.next || !form.confirm}>
            {loading ? 'Updating...' : 'Update password'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── Confirm Dialog ───────────────────────────────────────────────────────────

function ConfirmDialog({ open, title, message, confirmLabel = 'Delete', onConfirm, onCancel }: {
  open: boolean
  title: string
  message: string
  confirmLabel?: string
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onCancel() }}>
      <DialogContent style={{ maxWidth: 400 }}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: '4px 0 8px' }}>{message}</p>
        <DialogFooter style={{ gap: 8 }}>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button variant="destructive" onClick={onConfirm}>{confirmLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── Edit Project Dialog ──────────────────────────────────────────────────────

const SUGGESTED_SECRET_NAMES = [
  'saviynt_username', 'saviynt_password',
  'client_id', 'client_secret', 'api_key', 'api_token',
  'basic_username', 'basic_password',
]

function EditProjectDialog({ project, open, onClose, onUpdate }: {
  project: any | null; open: boolean; onClose: () => void; onUpdate: (updated: any) => void
}) {
  const [tab, setTab] = useState<'settings' | 'secrets' | 'deployment'>('settings')
  const [form, setForm] = useState({
    name: '', description: '', target_system: '',
    saviynt_url: '', saviynt_username: '', saviynt_password: '', connection_name: '',
    deployment_mode: 'direct', terraform_repo: '', terraform_branch: '',
    terraform_path: '', terraform_token_secret: '',
  })
  const [loading, setLoading] = useState(false)

  // Secrets state
  const [secrets, setSecrets] = useState<any[]>([])
  const [secretsLoading, setSecretsLoading] = useState(false)
  const [newSecret, setNewSecret] = useState({ name: '', value: '', description: '' })
  const [showSecretValue, setShowSecretValue] = useState(false)
  const [addingSecret, setAddingSecret] = useState(false)

  useEffect(() => {
    if (project) {
      setForm({
        name: project.name || '',
        description: project.description || '',
        target_system: project.target_system || '',
        saviynt_url: project.saviynt_url || '',
        saviynt_username: project.saviynt_username || '',
        saviynt_password: '',
        connection_name: project.connection_name || '',
        deployment_mode: project.deployment_mode || 'direct',
        terraform_repo: project.terraform_repo || '',
        terraform_branch: project.terraform_branch || 'dev',
        terraform_path: project.terraform_path || 'connections',
        terraform_token_secret: project.terraform_token_secret || '',
      })
      // Load secrets when dialog opens
      setSecretsLoading(true)
      apiGet(`/api/projects/${project.id}/secrets`)
        .then(setSecrets)
        .catch(() => toast.error('Failed to load secrets'))
        .finally(() => setSecretsLoading(false))
    }
  }, [project])

  const handleSave = async () => {
    if (!form.name || !project) return
    setLoading(true)
    try {
      const updated = await apiPatch(`/api/projects/${project.id}`, {
        name: form.name,
        description: form.description || null,
        target_system: form.target_system || null,
        saviynt_url: form.saviynt_url || null,
        ...(form.saviynt_username.trim() ? { saviynt_username: form.saviynt_username.trim() } : {}),
        ...(form.saviynt_password ? { saviynt_password: form.saviynt_password } : {}),
        connection_name: form.connection_name || null,
        deployment_mode: form.deployment_mode || 'direct',
        terraform_repo: form.terraform_repo || null,
        terraform_branch: form.terraform_branch || null,
        terraform_path: form.terraform_path || null,
        terraform_token_secret: form.terraform_token_secret || null,
      })
      if (form.saviynt_username.trim() || form.saviynt_password) {
        const refreshedSecrets = await apiGet(`/api/projects/${project.id}/secrets`)
        setSecrets(refreshedSecrets)
      }
      setForm(f => ({ ...f, saviynt_password: '' }))
      onUpdate(updated)
      toast.success('Project updated')
      onClose()
    } catch (e: any) { toast.error(e.message || 'Update failed') }
    finally { setLoading(false) }
  }

  const handleAddSecret = async () => {
    if (!newSecret.name || !newSecret.value || !project) return
    setAddingSecret(true)
    try {
      const created = await apiPost(`/api/projects/${project.id}/secrets`, {
        secret_name: newSecret.name,
        secret_value: newSecret.value,
        description: newSecret.description || null,
      })
      setSecrets(prev => {
        const exists = prev.findIndex(s => s.secret_name === created.secret_name)
        return exists >= 0
          ? prev.map((s, i) => i === exists ? created : s)
          : [...prev, created]
      })
      setNewSecret({ name: '', value: '', description: '' })
      setShowSecretValue(false)
      toast.success(`Secret "${created.secret_name}" saved`)
    } catch (e: any) { toast.error(e.message || 'Failed to save secret') }
    finally { setAddingSecret(false) }
  }

  const handleDeleteSecret = async (secretName: string) => {
    if (!project) return
    try {
      await apiDelete(`/api/projects/${project.id}/secrets/${encodeURIComponent(secretName)}`)
      setSecrets(prev => prev.filter(s => s.secret_name !== secretName))
      toast.success(`Secret "${secretName}" deleted`)
    } catch (e: any) { toast.error(e.message || 'Failed to delete secret') }
  }

  const field = (label: string, key: keyof typeof form, placeholder?: string) => (
    <div>
      <label style={{ display: 'block', fontSize: 11.5, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>
        {label}
      </label>
      <Input value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        placeholder={placeholder} />
    </div>
  )

  const hasSecret = (name: string) => secrets.some(s => s.secret_name === name)

  const tabStyle = (t: 'settings' | 'secrets' | 'deployment') => ({
    padding: '6px 14px',
    fontSize: 12.5,
    fontWeight: 500,
    border: 'none',
    borderBottom: tab === t ? '2px solid var(--accent)' : '2px solid transparent',
    background: 'none',
    color: tab === t ? 'var(--text-primary)' : 'var(--text-muted)',
    cursor: 'pointer',
    transition: 'color 0.15s',
  } as React.CSSProperties)

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { setTab('settings'); onClose() } }}>
      <DialogContent style={{ maxWidth: 520 }}>
        <DialogHeader><DialogTitle>Edit project</DialogTitle></DialogHeader>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: 16, marginTop: -4 }}>
          <button style={tabStyle('settings')} onClick={() => setTab('settings')}>Settings</button>
          <button style={tabStyle('deployment')} onClick={() => setTab('deployment')}>Deployment</button>
          <button style={tabStyle('secrets')} onClick={() => setTab('secrets')}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <Shield size={11} />
              Secrets
              {secrets.length > 0 && (
                <span style={{ background: 'var(--accent)', color: '#fff', borderRadius: 8, padding: '0 5px', fontSize: 10, fontWeight: 600 }}>
                  {secrets.length}
                </span>
              )}
            </span>
          </button>
        </div>

        {tab === 'settings' && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {field('Project name *', 'name', 'e.g. ServiceNow Production Connector')}
              {field('Description', 'description', 'What is this connector for?')}
              {field('Target system', 'target_system', 'e.g. ServiceNow, Workday, Custom REST API')}
              {field('Saviynt instance URL', 'saviynt_url', 'https://myorg.saviyntcloud.com')}
              <div style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface-0)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
                  <div>
                    <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>Saviynt credentials</p>
                    <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--text-muted)' }}>
                      Saved as encrypted secrets named <code>saviynt_username</code> and <code>saviynt_password</code>.
                    </p>
                  </div>
                  <span style={{
                    fontSize: 10.5, color: hasSecret('saviynt_username') && hasSecret('saviynt_password') ? 'var(--accent)' : 'var(--warning)',
                    border: `1px solid ${hasSecret('saviynt_username') && hasSecret('saviynt_password') ? 'rgba(46,204,113,0.3)' : 'rgba(245,158,11,0.3)'}`,
                    borderRadius: 999, padding: '3px 7px', whiteSpace: 'nowrap',
                  }}>
                    {hasSecret('saviynt_username') && hasSecret('saviynt_password') ? 'Configured' : 'Incomplete'}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11.5, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>
                      Username
                    </label>
                    <Input
                      value={form.saviynt_username}
                      onChange={e => setForm(f => ({ ...f, saviynt_username: e.target.value }))}
                      placeholder={hasSecret('saviynt_username') ? 'Configured — enter to replace' : project?.saviynt_username ? 'Legacy value exists — enter to migrate' : 'svc_saviynt'}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11.5, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>
                      Password
                    </label>
                    <Input
                      type="password"
                      value={form.saviynt_password}
                      onChange={e => setForm(f => ({ ...f, saviynt_password: e.target.value }))}
                      placeholder={hasSecret('saviynt_password') ? 'Configured — enter to replace' : 'Enter password'}
                    />
                  </div>
                </div>
              </div>
              {field('Connection name', 'connection_name', 'e.g. ServiceNow_Production')}
            </div>
            <DialogFooter style={{ marginTop: 16 }}>
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button onClick={handleSave} disabled={loading || !form.name}>
                {loading ? 'Saving...' : 'Save changes'}
              </Button>
            </DialogFooter>
          </>
        )}

        {tab === 'deployment' && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Deployment mode */}
              <div>
                <label style={{ display: 'block', fontSize: 11.5, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>
                  Deployment mode
                </label>
                <select
                  value={form.deployment_mode}
                  onChange={e => setForm(f => ({ ...f, deployment_mode: e.target.value }))}
                  style={{ width: '100%', padding: '7px 10px', fontSize: 13, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface-1)', color: 'var(--text-primary)', outline: 'none' }}
                >
                  <option value="direct">Direct — push config directly to Saviynt API</option>
                  <option value="terraform">Terraform / GitOps — generate HCL and push to GitHub</option>
                </select>
              </div>

              {form.deployment_mode === 'terraform' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 14, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface-0)' }}>
                  <p style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-secondary)', margin: 0 }}>GitHub / Terraform settings</p>

                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
                      Repository <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(e.g. org/saviynt-terraform-configs)</span>
                    </label>
                    <Input
                      value={form.terraform_repo}
                      onChange={e => setForm(f => ({ ...f, terraform_repo: e.target.value }))}
                      placeholder="org/saviynt-terraform-configs"
                    />
                  </div>

                  <div style={{ display: 'flex', gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Branch</label>
                      <Input
                        value={form.terraform_branch}
                        onChange={e => setForm(f => ({ ...f, terraform_branch: e.target.value }))}
                        placeholder="dev"
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
                        Path <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(folder in repo)</span>
                      </label>
                      <Input
                        value={form.terraform_path}
                        onChange={e => setForm(f => ({ ...f, terraform_path: e.target.value }))}
                        placeholder="connections"
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
                      GitHub token secret <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(name of the project secret holding your GitHub PAT)</span>
                    </label>
                    {secrets.length > 0 ? (
                      <select
                        value={form.terraform_token_secret}
                        onChange={e => setForm(f => ({ ...f, terraform_token_secret: e.target.value }))}
                        style={{ width: '100%', padding: '7px 10px', fontSize: 13, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface-1)', color: 'var(--text-primary)', outline: 'none' }}
                      >
                        <option value="">— select a secret —</option>
                        {secrets.map(s => (
                          <option key={s.secret_name} value={s.secret_name}>{s.secret_name}</option>
                        ))}
                      </select>
                    ) : (
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                        No secrets yet — add your GitHub PAT in the Secrets tab first, then select it here.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter style={{ marginTop: 16 }}>
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button onClick={handleSave} disabled={loading || !form.name}>
                {loading ? 'Saving...' : 'Save changes'}
              </Button>
            </DialogFooter>
          </>
        )}

        {tab === 'secrets' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
              Store credentials here and reference them in your JSONs using <code style={{ fontSize: 11 }}>{'{{secret:name}}'}</code>.
              Values are never shown after saving and are resolved server-side — the agent only sees secret names.
            </p>

            {/* Existing secrets list */}
            {secretsLoading ? (
              <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading secrets…</p>
            ) : secrets.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {secrets.map(s => (
                  <div key={s.secret_name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', borderRadius: 6, background: 'var(--surface-1)', border: '1px solid var(--border)' }}>
                    <div>
                      <span style={{ fontSize: 12.5, fontWeight: 600, fontFamily: 'var(--font-mono)' }}>{s.secret_name}</span>
                      {s.description && <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>{s.description}</span>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>••••••••</span>
                      <button
                        onClick={() => handleDeleteSecret(s.secret_name)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2, display: 'flex', alignItems: 'center' }}
                        title="Delete secret" aria-label={`Delete secret ${s.secret_name}`}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>No secrets configured yet.</p>
            )}

            {/* Add / update a secret */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 12, borderRadius: 8, border: '1px dashed var(--border)', background: 'var(--surface-0)' }}>
              <p style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-secondary)', margin: 0 }}>Add or update a secret</p>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Secret name</label>
                  <Input
                    list="secret-name-suggestions"
                    value={newSecret.name}
                    onChange={e => setNewSecret(s => ({ ...s, name: e.target.value }))}
                    placeholder="e.g. client_secret"
                  />
                  <datalist id="secret-name-suggestions">
                    {SUGGESTED_SECRET_NAMES.map(n => <option key={n} value={n} />)}
                  </datalist>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Value</label>
                  <div style={{ position: 'relative' }}>
                    <Input
                      type={showSecretValue ? 'text' : 'password'}
                      value={newSecret.value}
                      onChange={e => setNewSecret(s => ({ ...s, value: e.target.value }))}
                      placeholder="••••••••"
                      style={{ paddingRight: 32 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowSecretValue(v => !v)}
                      aria-label={showSecretValue ? 'Hide secret value' : 'Show secret value'}
                      style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0, display: 'flex' }}
                    >
                      {showSecretValue ? <EyeOff size={13} /> : <Eye size={13} />}
                    </button>
                  </div>
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Description (optional)</label>
                <Input
                  value={newSecret.description}
                  onChange={e => setNewSecret(s => ({ ...s, description: e.target.value }))}
                  placeholder='e.g. "OAuth2 client secret for target API"'
                />
              </div>
              <Button
                size="sm"
                onClick={handleAddSecret}
                disabled={addingSecret || !newSecret.name || !newSecret.value}
                style={{ alignSelf: 'flex-end' }}
              >
                {addingSecret ? 'Saving…' : secrets.some(s => s.secret_name === newSecret.name) ? 'Update secret' : 'Add secret'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

// ─── Forgot Password Page ────────────────────────────────────────────────────

function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)

  const handleSubmit = async () => {
    if (!email) return
    setLoading(true)
    try {
      await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      // Always show success — backend never reveals whether email exists
      setSent(true)
    } catch {
      setSent(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="auth-page-v2">
      <div className="auth-bg-grid" />
      <div className="auth-bg-glow" />
      <motion.div className="auth-card-v2" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
        <div className="auth-logo-row">
          <div className="auth-logo-icon">
            <img src="/logo.svg" alt="" style={{ width: 22, height: 22, borderRadius: 4 }} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)' }}>Saviynt Configurator</span>
        </div>

        {sent ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <CheckCircle2 size={22} style={{ color: 'var(--accent)', flexShrink: 0 }} />
              <h1 style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.03em', margin: 0 }}>Check your email</h1>
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 28 }}>
              If <strong style={{ color: 'var(--text-primary)' }}>{email}</strong> is registered,
              you'll receive a password reset link shortly. The link expires in 1 hour.
            </p>
            <a href="/login" style={{ display: 'block', textAlign: 'center', fontSize: 13, color: 'var(--accent)', textDecoration: 'none', marginTop: 8 }}>
              Back to sign in
            </a>
          </>
        ) : (
          <>
            <h1 style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.03em', marginBottom: 6 }}>Forgot password?</h1>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 28 }}>
              Enter your email and we'll send you a reset link.
            </p>
            <div className="auth-input-wrapper">
              <Mail size={14} className="auth-input-icon" />
              <input className="auth-input-v2" type="email" value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                placeholder="you@company.com" autoFocus autoComplete="email" />
            </div>
            <button className="btn-primary-v2" onClick={handleSubmit} disabled={loading || !email}>
              {loading ? 'Sending…' : 'Send reset link'}
            </button>
            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <a href="/login" style={{ fontSize: 13, color: 'var(--text-muted)', textDecoration: 'none' }}>
                Back to sign in
              </a>
            </div>
          </>
        )}
      </motion.div>
    </div>
  )
}

// ─── Reset Password Page ──────────────────────────────────────────────────────

function ResetPasswordPage() {
  const token = new URLSearchParams(window.location.search).get('token') || ''
  const [form, setForm] = useState({ password: '', confirm: '' })
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)

  const handleSubmit = async () => {
    if (form.password !== form.confirm) { toast.error('Passwords do not match'); return }
    if (form.password.length < 8) { toast.error('Password must be at least 8 characters'); return }
    setLoading(true)
    try {
      const r = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: form.password }),
      })
      const data = await r.json()
      if (!r.ok) throw new Error(data.detail || 'Reset failed')
      setDone(true)
    } catch (e: any) {
      toast.error(e.message || 'Password reset failed')
    } finally {
      setLoading(false)
    }
  }

  if (!token) {
    return (
      <div className="auth-page-v2">
        <div className="auth-bg-grid" /><div className="auth-bg-glow" />
        <motion.div className="auth-card-v2" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Invalid reset link. <a href="/forgot-password" style={{ color: 'var(--accent)' }}>Request a new one</a>.</p>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="auth-page-v2">
      <div className="auth-bg-grid" />
      <div className="auth-bg-glow" />
      <motion.div className="auth-card-v2" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
        <div className="auth-logo-row">
          <div className="auth-logo-icon">
            <img src="/logo.svg" alt="" style={{ width: 22, height: 22, borderRadius: 4 }} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)' }}>Saviynt Configurator</span>
        </div>

        {done ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <CheckCircle2 size={22} style={{ color: 'var(--accent)', flexShrink: 0 }} />
              <h1 style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.03em', margin: 0 }}>Password updated</h1>
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 28 }}>
              Your password has been changed. You can now sign in with your new password.
            </p>
            <a href="/login" className="btn-primary-v2" style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}>
              Sign in
            </a>
          </>
        ) : (
          <>
            <h1 style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.03em', marginBottom: 6 }}>Set new password</h1>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 28 }}>
              Choose a new password for your account.
            </p>
            <div className="auth-input-wrapper">
              <Lock size={14} className="auth-input-icon" />
              <input className="auth-input-v2" type="password" value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                placeholder="New password (min 8 chars)" autoFocus />
            </div>
            <div className="auth-input-wrapper" style={{ marginTop: 10 }}>
              <Lock size={14} className="auth-input-icon" />
              <input className="auth-input-v2" type="password" value={form.confirm}
                onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                placeholder="Confirm new password" />
            </div>
            <button className="btn-primary-v2" onClick={handleSubmit} disabled={loading || !form.password || !form.confirm}>
              {loading ? 'Updating…' : 'Update password'}
            </button>
          </>
        )}
      </motion.div>
    </div>
  )
}

// ─── Auth Page ────────────────────────────────────────────────────────────────

function AuthPage() {
  const { setAuth, setAccessToken } = useStore()
  const navigate = useNavigate()
  const [mode, setMode] = useState<'login' | 'register'>('login')
  const [form, setForm] = useState({ email: '', password: '', displayName: '', orgId: '' })
  const [orgs, setOrgs] = useState<{ id: string; name: string }[]>([])
  const [orgsError, setOrgsError] = useState(false)
  const [orgsLoading, setOrgsLoading] = useState(false)
  const [loading, setLoading] = useState(false)

  const fetchOrgs = useCallback(async () => {
    setOrgsLoading(true)
    setOrgsError(false)
    try {
      const data = await fetch(`${API_BASE}/api/auth/organizations`).then(r => r.json())
      setOrgs(data)
    } catch {
      setOrgs([])
      setOrgsError(true)
    } finally {
      setOrgsLoading(false)
    }
  }, [])

  // Fetch orgs as soon as the register form becomes visible
  useEffect(() => {
    if (mode === 'register') fetchOrgs()
  }, [mode, fetchOrgs])

  const handleSubmit = async () => {
    if (mode === 'register') {
      if (!form.orgId) { toast.error('Please select an organization'); return }
      if (!form.displayName.trim()) { toast.error('Please enter your name'); return }
      if (!form.email.trim()) { toast.error('Please enter your email'); return }
      if (!form.password) { toast.error('Please enter a password'); return }
      if (form.password.length < 8) { toast.error('Password must be at least 8 characters'); return }
    }
    setLoading(true)
    try {
      if (mode === 'login') {
        const data = await apiPost('/api/auth/login', { email: form.email, password: form.password })
        setAccessToken(data.access_token)
        setAuth({ id: data.user_id, email: data.email, org_id: data.org_id, role: data.role ?? 'member', status: data.status ?? 'pending' })
        if (data.status === 'pending') {
          toast('Your account is awaiting admin approval. You\'ll be notified once access is granted.', { duration: 5000 })
        } else if (data.status === 'suspended') {
          toast.error('Your account has been suspended. Contact your administrator.')
        } else {
          navigate('/projects')
        }
      } else {
        await apiPost('/api/auth/register', {
          email: form.email, password: form.password,
          display_name: form.displayName, org_id: form.orgId
        })
        toast('Registration submitted. Your account is pending admin approval — you\'ll receive access once reviewed.', { duration: 6000 })
        setMode('login')
      }
    } catch (e: any) {
      toast.error(e.message || 'Authentication failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="auth-page-v2">
      <div className="auth-bg-grid" />
      <div className="auth-bg-glow" />

      <motion.div
        className="auth-card-v2"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        {/* Logo row */}
        <div className="auth-logo-row">
          <div className="auth-logo-icon">
            <img src="/logo.svg" alt="" style={{ width: 22, height: 22, borderRadius: 4 }} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)' }}>
            Saviynt Configurator
          </span>
        </div>

        <h1 style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.03em', marginBottom: 6 }}>
          {mode === 'login' ? 'Welcome back' : 'Create your account'}
        </h1>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 28 }}>
          {mode === 'login'
            ? 'Sign in to configure your Saviynt connectors'
            : 'Set up your organization to get started'}
        </p>

        {mode === 'register' && (
          <>
            <div className="auth-input-wrapper">
              <Building2 size={14} className="auth-input-icon" />
              <select className="auth-input-v2" value={form.orgId}
                onChange={e => setForm(f => ({ ...f, orgId: e.target.value }))}
                disabled={orgsLoading}
                style={{ cursor: 'pointer' }}>
                <option value="">
                  {orgsLoading ? 'Loading organizations…' : orgsError ? 'Could not load — check connection' : 'Select organization'}
                </option>
                {orgs.map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
              {orgsError && (
                <button type="button" onClick={fetchOrgs}
                  style={{ position: 'absolute', right: 32, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--accent)', fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  Retry
                </button>
              )}
            </div>
            <div className="auth-input-wrapper">
              <User size={14} className="auth-input-icon" />
              <input className="auth-input-v2" value={form.displayName}
                onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}
                placeholder="Your name" />
            </div>
          </>
        )}

        <div className="auth-input-wrapper">
          <Mail size={14} className="auth-input-icon" />
          <input className="auth-input-v2" type="email" value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            placeholder="you@company.com" autoComplete="email" />
        </div>

        <div className="auth-input-wrapper">
          <Lock size={14} className="auth-input-icon" />
          <input className="auth-input-v2" type="password" value={form.password}
            onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
            placeholder="••••••••" autoComplete="current-password"
            onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
        </div>

        {mode === 'login' && (
          <div style={{ textAlign: 'right', marginTop: -8, marginBottom: 4 }}>
            <a href="/forgot-password" style={{ fontSize: 12, color: 'var(--text-muted)', textDecoration: 'none' }}
              onMouseEnter={e => (e.currentTarget.style.color = 'var(--accent)')}
              onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-muted)')}>
              Forgot password?
            </a>
          </div>
        )}

        <button className="btn-primary-v2" onClick={handleSubmit}
          disabled={loading || (mode === 'register' && (!form.orgId || !form.displayName.trim() || !form.email.trim() || !form.password))}>
          {loading ? 'Loading...' : mode === 'login' ? 'Sign in' : 'Create account'}
        </button>

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: 'var(--text-secondary)' }}>
          {mode === 'login' ? (
            <>Don't have an account?{' '}
              <button onClick={() => setMode('register')} style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 13 }}>
                Sign up
              </button>
            </>
          ) : (
            <>Already have an account?{' '}
              <button onClick={() => setMode('login')} style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 13 }}>
                Sign in
              </button>
            </>
          )}
        </div>
      </motion.div>
    </div>
  )
}

// ─── New Project Modal ────────────────────────────────────────────────────────

function NewProjectModal({ open, onClose, onCreate }: { open: boolean; onClose: () => void; onCreate: (p: any) => void }) {
  const [form, setForm] = useState({ name: '', targetSystem: '', saviyntUrl: '' })
  const [loading, setLoading] = useState(false)

  const handleCreate = async () => {
    if (!form.name) return
    setLoading(true)
    try {
      const project = await apiPost('/api/projects/', {
        name: form.name,
        target_system: form.targetSystem || undefined,
        saviynt_url: form.saviyntUrl || undefined
      })
      onCreate(project)
      onClose()
      setForm({ name: '', targetSystem: '', saviyntUrl: '' })
    } catch (e: any) { toast.error(e.message) }
    finally { setLoading(false) }
  }

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New Project</DialogTitle>
        </DialogHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ display: 'block', fontSize: 11.5, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>
              Project name *
            </label>
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. ServiceNow Production Connector" autoFocus />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11.5, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>
              Target system
            </label>
            <Input value={form.targetSystem} onChange={e => setForm(f => ({ ...f, targetSystem: e.target.value }))}
              placeholder="e.g. ServiceNow, Workday, Custom REST API" />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11.5, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>
              Saviynt instance URL
            </label>
            <Input value={form.saviyntUrl} onChange={e => setForm(f => ({ ...f, saviyntUrl: e.target.value }))}
              placeholder="https://myorg.saviyntcloud.com" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleCreate} disabled={loading || !form.name}>
            {loading ? 'Creating...' : 'Create project'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── Shared sign-out helper ───────────────────────────────────────────────────

async function signOut(clearAuth: () => void) {
  await apiPost('/api/auth/logout', {}).catch(() => {})
  await supabase.auth.signOut().catch(() => {})
  clearAuth()
}

// ─── Projects Page ────────────────────────────────────────────────────────────

function ProjectsPage() {
  const { projects, setProjects, clearAuth, user } = useStore()
  const navigate = useNavigate()
  const [showModal, setShowModal] = useState(false)
  const [showChangePw, setShowChangePw] = useState(false)
  const [editingProject, setEditingProject] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [pendingDeleteProject, setPendingDeleteProject] = useState<{ id: string; name: string } | null>(null)
  const [deletingProjectId, setDeletingProjectId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const isAdmin = user?.role === 'admin' && (!SUPER_ADMIN_EMAIL || user?.email === SUPER_ADMIN_EMAIL)

  const filteredProjects = search.trim()
    ? projects.filter(p =>
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        (p.description ?? '').toLowerCase().includes(search.toLowerCase()) ||
        (p.target_system ?? '').toLowerCase().includes(search.toLowerCase()) ||
        (p.connection_name ?? '').toLowerCase().includes(search.toLowerCase())
      )
    : projects

  const getProjectSignals = (p: any) => {
    const text = `${p.name || ''} ${p.description || ''} ${p.target_system || ''} ${p.connection_name || ''}`.toLowerCase()
    const connectorTypes = [
      { label: 'SOAP', match: /soap|wsdl|soapaction/.test(text) },
      { label: 'JDBC', match: /jdbc|database|oracle|sql server|mysql|postgres|db2|snowflake/.test(text) },
      { label: 'AD/LDAP', match: /active directory|ldap|ldaps|endpoints_filter/.test(text) },
      { label: 'REST', match: /rest|api|oauth|openapi|postman/.test(text) },
    ].filter(t => t.match).map(t => t.label)
    const status = p.status || (p.connection_name ? 'draft' : 'setup')
    const needsSetup = !p.saviynt_url || !(p as any).secret_count
    return {
      connectorTypes: connectorTypes.length ? connectorTypes : ['Unknown'],
      status,
      needsSetup,
      statusLabel: status === 'deployed' ? 'Deployed' : status === 'tested' ? 'Tested' : status === 'failed' ? 'Failed' : needsSetup ? 'Needs setup' : 'Draft',
    }
  }

  const metrics = {
    total: projects.length,
    needsSetup: projects.filter(p => getProjectSignals(p).needsSetup).length,
    withSecrets: projects.filter(p => ((p as any).secret_count || 0) > 0).length,
    withInstance: projects.filter(p => !!p.saviynt_url).length,
  }

  useEffect(() => {
    apiGet('/api/projects/').then(setProjects).catch(() => toast.error('Failed to load projects')).finally(() => setLoading(false))
  }, [setProjects])

  const startProject = async (project: any) => {
    try {
      const convs = await apiGet(`/api/projects/${project.id}/conversations`)
      if (convs && convs.length > 0) navigate(`/project/${project.id}/chat/${convs[0].id}`)
      else {
        const conv = await apiPost('/api/conversations/', { project_id: project.id, title: '' })
        navigate(`/project/${project.id}/chat/${conv.id}`)
      }
    } catch (e: any) { toast.error(e.message) }
  }

  const confirmDeleteProject = async () => {
    if (!pendingDeleteProject) return
    const { id, name } = pendingDeleteProject
    setPendingDeleteProject(null)
    setDeletingProjectId(id)
    try {
      await apiDelete(`/api/projects/${id}`)
      setProjects(projects.filter(p => p.id !== id))
      toast.success(`"${name}" deleted`)
    } catch (e: any) { toast.error(e.message || 'Delete failed') }
    finally { setDeletingProjectId(null) }
  }

  return (
    <TooltipProvider>
      <div className="projects-page-v2">
        {/* Sticky nav */}
        <div className="projects-topbar">
          <div className="projects-topbar-left">
            <img src="/logo.svg" style={{ width: 26, height: 26, borderRadius: 6 }} alt="" />
            <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
              Saviynt Configurator
            </span>
          </div>
          <div className="projects-topbar-right">
            {isAdmin && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
                    <ShieldAlert size={13} /> Admin
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Feedback &amp; RAG admin panel</TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" onClick={() => setShowChangePw(true)}>
                  <KeyRound size={13} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Change password</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" onClick={() => signOut(clearAuth)}>
                  <LogOut size={13} /> Sign out
                </Button>
              </TooltipTrigger>
              <TooltipContent>{user?.email}</TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* Workspace header */}
        <div className="projects-workspace-header">
          <div>
            <motion.h1 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
              Projects
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              Resume connector builds, review readiness, and start new Saviynt integrations.
            </motion.p>
          </div>
        </div>

        <div className="projects-metrics">
          {[
            { label: 'Total projects', value: metrics.total, hint: 'All projects you can access' },
            { label: 'Missing setup', value: metrics.needsSetup, hint: 'No Saviynt URL or no secrets' },
            { label: 'With secrets', value: metrics.withSecrets, hint: 'At least one stored project secret' },
            { label: 'With Saviynt URL', value: metrics.withInstance, hint: 'Tenant URL configured' },
          ].map(item => (
            <div key={item.label} className="projects-metric-card">
              <strong>{item.value}</strong>
              <span>{item.label}</span>
              <p>{item.hint}</p>
            </div>
          ))}
        </div>

        {/* Grid */}
        <div className="projects-body">
          <div className="projects-list-header">
            <div>
              <span className="projects-section-label">Your projects</span>
              <p>{filteredProjects.length} shown</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ position: 'relative' }}>
                <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search projects…"
                  style={{
                    background: 'var(--bg-raised)', border: '1px solid var(--border)',
                    borderRadius: 7, padding: '5px 10px 5px 28px',
                    fontSize: 12.5, color: 'var(--text-primary)', outline: 'none',
                    width: 200, transition: 'border-color 0.15s',
                  }}
                  onFocus={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                  onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'}
                />
              </div>
              <Button size="sm" onClick={() => setShowModal(true)}>
                <Plus size={12} /> New project
              </Button>
            </div>
          </div>

          {loading ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading projects...</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(268px,1fr))', gap: 12 }}>
              {filteredProjects.length === 0 && search.trim() ? (
                <p style={{ color: 'var(--text-muted)', fontSize: 13, gridColumn: '1/-1' }}>
                  No projects match "<span style={{ color: 'var(--text-secondary)' }}>{search}</span>"
                </p>
              ) : null}
              {filteredProjects.map((p, i) => (
                <ProjectCard
                  key={p.id}
                  project={p}
                  index={i}
                  signals={getProjectSignals(p)}
                  user={user}
                  deletingProjectId={deletingProjectId}
                  onOpen={() => startProject(p)}
                  onEdit={() => setEditingProject(p)}
                  onDelete={() => setPendingDeleteProject({ id: p.id, name: p.name })}
                />
              ))}

              {projects.length === 0 && (
                <motion.div
                  className="project-card-v2 project-card-empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  onClick={() => setShowModal(true)}
                >
                  <Plus size={20} />
                  <span>Create your first project</span>
                  <p>Start with REST, SOAP, JDBC, AD, or LDAP connector work.</p>
                </motion.div>
              )}
            </div>
          )}
        </div>

        <NewProjectModal open={showModal} onClose={() => setShowModal(false)} onCreate={p => setProjects([p, ...projects])} />
        <ChangePasswordDialog open={showChangePw} onClose={() => setShowChangePw(false)} />
        <EditProjectDialog
          project={editingProject}
          open={!!editingProject}
          onClose={() => setEditingProject(null)}
          onUpdate={updated => { setProjects(projects.map(p => p.id === updated.id ? updated : p)); setEditingProject(null) }}
        />
        <ConfirmDialog
          open={!!pendingDeleteProject}
          title="Delete project?"
          message={pendingDeleteProject
            ? `"${pendingDeleteProject.name}" will be permanently deleted — including all conversations, messages, secrets, and any API docs uploaded to this project. This cannot be undone.`
            : ''}
          confirmLabel="Delete project"
          onConfirm={confirmDeleteProject}
          onCancel={() => setPendingDeleteProject(null)}
        />
      </div>
    </TooltipProvider>
  )
}

function ProjectCard({ project: p, index, signals, user, deletingProjectId, onOpen, onEdit, onDelete }: {
  project: any
  index: number
  signals: { connectorTypes: string[]; status: string; statusLabel: string; needsSetup: boolean }
  user: any
  deletingProjectId: string | null
  onOpen: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  const hostname = (() => {
    try { return p.saviynt_url ? new URL(p.saviynt_url).hostname : null }
    catch { return p.saviynt_url }
  })()

  return (
    <motion.div
      className="project-card-v2"
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }} onClick={onOpen}
    >
      <div className="project-card-status-row">
        <span className={`project-status-pill ${signals.status === 'deployed' ? 'deployed' : signals.status === 'tested' ? 'tested' : signals.status === 'failed' ? 'failed' : signals.needsSetup ? 'needs' : 'draft'}`}>
          <span /> {signals.statusLabel}
        </span>
        <div className="project-card-actions">
          <button
            onClick={e => { e.stopPropagation(); onEdit() }}
            title="Edit project" aria-label="Edit project"
          >
            <Edit2 size={12} />
          </button>
          {(user?.role === 'admin' || p.created_by === user?.id) && (
            <button
              onClick={e => { e.stopPropagation(); onDelete() }}
              disabled={deletingProjectId === p.id}
              title="Delete project" aria-label="Delete project"
            >
              {deletingProjectId === p.id ? <span style={{ fontSize: 10 }}>…</span> : <Trash2 size={12} />}
            </button>
          )}
          <ExternalLink size={12} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
        </div>
      </div>

      <div className="project-card-main">
        <div className="project-card-type-dot" />
        <div>
          <h3>{p.name}</h3>
          <p>{p.description || p.connection_name || 'No description yet'}</p>
        </div>
      </div>

      <div className="project-chip-row">
        {p.target_system && <span className="project-chip target">{p.target_system}</span>}
        {signals.connectorTypes.map(type => <span key={type} className="project-chip">{type}</span>)}
      </div>

      <div className="project-card-meta-grid">
        <div>
          <Database size={11} />
          <span>{hostname || 'No instance set'}</span>
        </div>
        <div>
          <Shield size={11} />
          <span>{(p as any).secret_count || 0} secrets</span>
        </div>
        {(p as any).created_by_name && (
          <div>
            <User size={11} />
            <span>{(p as any).created_by_name}</span>
          </div>
        )}
      </div>
    </motion.div>
  )
}

// ─── Workspace ────────────────────────────────────────────────────────────────

function Workspace() {
  const { projectId, conversationId } = useParams()
  const [project, setProject] = useState<any>(null)
  const [conversations, setConversations] = useState<any[]>([])
  const [configStatus, setConfigStatus] = useState<string>('draft')
  const [showChangePw, setShowChangePw] = useState(false)
  const [showEditProject, setShowEditProject] = useState(false)
  const [pendingDeleteConv, setPendingDeleteConv] = useState<string | null>(null)
  const [openConvMenuId, setOpenConvMenuId] = useState<string | null>(null)
  const [convMenuPos, setConvMenuPos] = useState<{ top: number; left: number } | null>(null)
  const [renamingConv, setRenamingConv] = useState<{ id: string; title: string } | null>(null)
  const convMenuRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { clearAuth, user } = useStore()
  const isAdmin = user?.role === 'admin' && (!SUPER_ADMIN_EMAIL || user?.email === SUPER_ADMIN_EMAIL)

  useEffect(() => {
    if (!projectId) return
    Promise.all([
      apiGet(`/api/projects/${projectId}`),
      apiGet(`/api/projects/${projectId}/conversations`),
      apiGet(`/api/projects/${projectId}/config`)
    ]).then(([proj, convs, config]) => {
      setProject(proj); setConversations(convs)
      if (config?.status) setConfigStatus(config.status)
    }).catch((e: any) => {
      if (e?.status === 404 || e?.status === 403) {
        toast.error('This project no longer exists.')
        navigate('/')
      } else {
        console.error(e)
      }
    })
  }, [projectId, navigate])

  const newConversation = async () => {
    if (!projectId) return
    const conv = await apiPost('/api/conversations/', { project_id: projectId, title: '' })
    setConversations(prev => [conv, ...prev])
    navigate(`/project/${projectId}/chat/${conv.id}`)
  }

  const updateConversationTitle = (convId: string, firstMessage: string) => {
    const title = firstMessage.slice(0, 50) + (firstMessage.length > 50 ? '...' : '')
    setConversations(prev => prev.map(c => c.id === convId ? { ...c, title } : c))
    apiPost(`/api/conversations/${convId}/title`, { title }).catch(() => {})
  }

  const deleteConversation = (e: React.MouseEvent, convId: string) => {
    e.stopPropagation()
    setPendingDeleteConv(convId)
  }

  const openConvMenu = (e: React.MouseEvent, conv: { id: string; title: string }) => {
    e.stopPropagation()
    if (openConvMenuId === conv.id) { setOpenConvMenuId(null); return }
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    setConvMenuPos({ top: rect.bottom + 4, left: Math.max(rect.right - 144, 8) })
    setOpenConvMenuId(conv.id)
  }

  const renameConversation = async (convId: string, newTitle: string) => {
    const title = newTitle.trim()
    if (!title) return
    setConversations(prev => prev.map(c => c.id === convId ? { ...c, title } : c))
    setRenamingConv(null)
    apiPost(`/api/conversations/${convId}/title`, { title }).catch(() => {})
  }

  // Close conv menu on outside click
  useEffect(() => {
    if (!openConvMenuId) return
    const handler = (e: MouseEvent) => {
      if (convMenuRef.current && !convMenuRef.current.contains(e.target as Node)) {
        setOpenConvMenuId(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [openConvMenuId])

  const confirmDeleteConversation = async () => {
    if (!pendingDeleteConv) return
    const convId = pendingDeleteConv
    setPendingDeleteConv(null)
    try {
      await apiPost(`/api/conversations/${convId}/delete`, {})
      setConversations(prev => prev.filter(c => c.id !== convId))
      if (conversationId === convId) navigate(`/project/${projectId}`)
    } catch { toast.error('Failed to delete conversation') }
  }

  const statusVariant: Record<string, any> = {
    draft: 'outline', tested: 'warning', deployed: 'accent', failed: 'destructive'
  }

  return (
    <TooltipProvider>
      <div className="app-shell">
        {/* Sidebar */}
        <aside className="sidebar">
          <div className="sidebar-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: project ? 10 : 0 }}>
              <img src="/logo.svg" className="logo-img" alt="" />
              <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--text-primary)' }}>
                Saviynt
              </span>
            </div>
            {project && (
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.35 }}>
                  {project.name}
                </div>
                {project.target_system && (
                  <div className="sidebar-project-badge" style={{ marginTop: 5 }}>
                    {project.target_system}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Conversations */}
          <div className="sidebar-section" style={{ flex: 1, overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 6px 8px' }}>
              <span className="sidebar-label" style={{ padding: 0 }}>Conversations</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button onClick={newConversation} aria-label="New conversation" style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', padding: 3, borderRadius: 4, transition: 'color 0.15s' }}>
                    <Plus size={13} />
                  </button>
                </TooltipTrigger>
                <TooltipContent>New conversation</TooltipContent>
              </Tooltip>
            </div>
            {conversations.map(conv => (
              <div
                key={conv.id}
                className={`sidebar-item ${conv.id === conversationId ? 'active' : ''}`}
                onClick={() => navigate(`/project/${projectId}/chat/${conv.id}`)}
                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              >
                <span className="item-dot" />
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12.5 }}>
                  {conv.title || 'New conversation'}
                </span>
                <button
                  onClick={e => openConvMenu(e, conv)}
                  aria-label="Conversation options"
                  aria-haspopup="true"
                  aria-expanded={openConvMenuId === conv.id}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '2px 4px', borderRadius: 4, display: 'flex', alignItems: 'center', flexShrink: 0, opacity: 0.45, transition: 'opacity 0.15s, color 0.15s' }}
                  className="conv-menu-btn"
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '1' }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.45' }}
                >
                  <MoreHorizontal size={13} />
                </button>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div style={{ padding: '6px 10px 10px', borderTop: '1px solid var(--border)' }}>
            <UsageBadge isAdmin={isAdmin} />
            <button className="sidebar-item" onClick={() => navigate('/projects')}>
              <FolderOpen size={13} /> All projects
            </button>
            {project && (
              <button className="sidebar-item" onClick={() => setShowEditProject(true)}>
                <Edit2 size={13} /> Edit project
              </button>
            )}
            {isAdmin && (
              <button className="sidebar-item" onClick={() => navigate('/admin')}>
                <ShieldAlert size={13} /> Admin panel
              </button>
            )}
            <button className="sidebar-item" onClick={() => setShowChangePw(true)}>
              <KeyRound size={13} /> Change password
            </button>
            <button className="sidebar-item" onClick={() => signOut(clearAuth)}>
              <LogOut size={13} /> Sign out
            </button>
          </div>
          <ChangePasswordDialog open={showChangePw} onClose={() => setShowChangePw(false)} />
          <EditProjectDialog
            project={project}
            open={showEditProject}
            onClose={() => setShowEditProject(false)}
            onUpdate={updated => { setProject(updated); setShowEditProject(false) }}
          />
          <ConfirmDialog
            open={!!pendingDeleteConv}
            title="Delete conversation"
            message="This conversation and all its messages will be permanently deleted."
            onConfirm={confirmDeleteConversation}
            onCancel={() => setPendingDeleteConv(null)}
          />

          {/* Conversation overflow menu — fixed-position portal so sidebar overflow:hidden doesn't clip it */}
          {openConvMenuId && convMenuPos && (
            <div
              ref={convMenuRef}
              role="menu"
              style={{
                position: 'fixed', top: convMenuPos.top, left: convMenuPos.left,
                zIndex: 9999, minWidth: 144,
                background: 'var(--bg-overlay)', border: '1px solid var(--border-active)',
                borderRadius: 'var(--radius-sm)', padding: 4,
                boxShadow: '0 4px 20px rgba(0,0,0,0.45)',
              }}
            >
              {[
                { label: 'Rename', icon: <Edit2 size={12} />, action: () => {
                  const conv = conversations.find(c => c.id === openConvMenuId)
                  setRenamingConv({ id: openConvMenuId, title: conv?.title || '' })
                  setOpenConvMenuId(null)
                }},
                { label: 'Delete', icon: <Trash2 size={12} />, danger: true, action: (e: React.MouseEvent) => {
                  deleteConversation(e, openConvMenuId)
                  setOpenConvMenuId(null)
                }},
              ].map(item => (
                <button
                  key={item.label}
                  role="menuitem"
                  onClick={(e) => { e.stopPropagation(); item.action(e as any) }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                    background: 'none', border: 'none', cursor: 'pointer',
                    padding: '6px 10px', borderRadius: 4, fontSize: 12.5,
                    color: item.danger ? 'var(--danger)' : 'var(--text-secondary)',
                    transition: 'background 0.1s, color 0.1s',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)' }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'none' }}
                >
                  {item.icon}{item.label}
                </button>
              ))}
            </div>
          )}

          {/* Rename conversation dialog */}
          <Dialog open={!!renamingConv} onOpenChange={v => { if (!v) setRenamingConv(null) }}>
            <DialogContent>
              <DialogHeader><DialogTitle>Rename conversation</DialogTitle></DialogHeader>
              <div style={{ padding: '4px 0 8px' }}>
                <Input
                  value={renamingConv?.title || ''}
                  onChange={e => setRenamingConv(r => r ? { ...r, title: e.target.value } : null)}
                  onKeyDown={e => { if (e.key === 'Enter' && renamingConv) renameConversation(renamingConv.id, renamingConv.title) }}
                  placeholder="Conversation title"
                  autoFocus
                />
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setRenamingConv(null)}>Cancel</Button>
                <Button onClick={() => renamingConv && renameConversation(renamingConv.id, renamingConv.title)}>Rename</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </aside>

        {/* Main */}
        <div className="main-content">
          <div className="topbar">
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{ fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer', transition: 'color 0.15s' }}
                onClick={() => navigate('/projects')}
              >
                Projects
              </span>
              <ChevronRight size={11} style={{ color: 'var(--text-muted)' }} />
              <span className="topbar-title">{project?.name}</span>
            </div>
            <Badge variant={statusVariant[configStatus] || 'outline'} style={{ gap: 5 }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor', display: 'inline-block' }} />
              {configStatus}
            </Badge>
          </div>

          {conversationId ? (
            <ChatView
              key={conversationId}
              conversationId={conversationId}
              projectName={project?.name || ''}
              project={project}
              conversationTitle={conversations.find(c => c.id === conversationId)?.title || ''}
              configStatus={configStatus}
              onConfigUpdate={setConfigStatus}
              onFirstMessage={msg => updateConversationTitle(conversationId, msg)}
            />
          ) : (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              Select a conversation or start a new one
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  )
}

// ─── Admin Page ───────────────────────────────────────────────────────────────

interface FeedbackRow {
  id: string; conversation_id: string; message_id: string; user_id?: string
  rating: 'up' | 'down'; comment: string; injected_to_rag: boolean
  created_at: string; message_content: string | null
}

interface AdminUserSummary {
  id: string
  display_name?: string | null
  email?: string | null
}

interface AdminUserRow extends AdminUserSummary {
  role: string
  status: 'pending' | 'approved' | 'suspended'
  created_at: string
}

const SOURCE_TYPE_LABELS: Record<string, string> = {
  saviynt_rest_guide: 'Saviynt REST Guide',
  saviynt_developer_handbook: 'Developer Handbook',
  saviynt_schema_guide: 'Schema Guide',
  customer_api_doc: 'Customer API Doc',
  openapi_spec: 'OpenAPI Spec',
  postman_collection: 'Postman Collection',
  custom_examples: 'Custom Examples',
}

function AdminPage() {
  const navigate = useNavigate()
  const [adminTab, setAdminTab] = useState<'feedback' | 'knowledge' | 'users' | 'usage'>('feedback')

  // ── Feedback state ────────────────────────────────────────────────────────
  const [rows, setRows] = useState<FeedbackRow[]>([])
  const [filter, setFilter] = useState<'all' | 'up' | 'down'>('down')
  const [loading, setLoading] = useState(true)
  const [injecting, setInjecting] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [correctionText, setCorrectionText] = useState('')
  const [feedbackUserMap, setFeedbackUserMap] = useState<Record<string, AdminUserSummary>>({})

  // ── Knowledge source delete confirm ──────────────────────────────────────
  const [pendingDeleteSource, setPendingDeleteSource] = useState<{ id: string; name: string } | null>(null)

  // ── Feedback delete confirm ───────────────────────────────────────────────
  const [pendingDeleteFeedback, setPendingDeleteFeedback] = useState<string | null>(null)
  const [deletingFeedback, setDeletingFeedback] = useState<string | null>(null)

  // ── Users state ───────────────────────────────────────────────────────────
  const [userRows, setUserRows] = useState<AdminUserRow[]>([])
  const [usersLoading, setUsersLoading] = useState(false)
  const [userFilter, setUserFilter] = useState<'pending' | 'all'>('pending')
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null)

  useEffect(() => {
    if (adminTab !== 'users') return
    setUsersLoading(true)
    const path = userFilter === 'pending' ? '/api/auth/users?status=pending' : '/api/auth/users'
    apiGet(path)
      .then(setUserRows)
      .catch(() => toast.error('Failed to load users'))
      .finally(() => setUsersLoading(false))
  }, [adminTab, userFilter])

  const updateUserStatus = async (userId: string, newStatus: 'approved' | 'suspended' | 'pending') => {
    setUpdatingUserId(userId)
    try {
      await apiPatch(`/api/auth/users/${userId}/status`, { status: newStatus })
      setUserRows(prev => prev.map(u => u.id === userId ? { ...u, status: newStatus } : u))
      toast.success(newStatus === 'approved' ? 'User approved' : newStatus === 'suspended' ? 'User suspended' : 'User set to pending')
    } catch (e: any) { toast.error(e.message || 'Failed to update user') }
    finally { setUpdatingUserId(null) }
  }

  const [sendingResetId, setSendingResetId] = useState<string | null>(null)
  const sendPasswordReset = async (userId: string) => {
    setSendingResetId(userId)
    try {
      await apiPost(`/api/auth/users/${userId}/send-reset`, {})
      toast.success('Password reset link sent')
    } catch (e: any) { toast.error(e.message || 'Failed to send reset link') }
    finally { setSendingResetId(null) }
  }

  // ── Knowledge base state ──────────────────────────────────────────────────
  const [sources, setSources] = useState<any[]>([])
  const [sourcesLoading, setSourcesLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [selectedSourceType, setSelectedSourceType] = useState('customer_api_doc')
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (adminTab !== 'knowledge') return
    setSourcesLoading(true)
    apiGet('/api/ingest/sources')
      .then(setSources)
      .catch(() => toast.error('Failed to load knowledge sources'))
      .finally(() => setSourcesLoading(false))
  }, [adminTab])

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('source_type', selectedSourceType)
      const result = await apiUpload('/api/ingest/file', formData)
      toast.success(`Ingested ${result.chunk_count} chunks from ${file.name}`)
      // Refresh the list
      const updated = await apiGet('/api/ingest/sources')
      setSources(updated)
    } catch (e: any) { toast.error(e.message || 'Upload failed') }
    finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleDeleteSource = (sourceId: string, fileName: string) => {
    setPendingDeleteSource({ id: sourceId, name: fileName })
  }

  const confirmDeleteSource = async () => {
    if (!pendingDeleteSource) return
    const { id } = pendingDeleteSource
    setPendingDeleteSource(null)
    try {
      await apiDelete(`/api/ingest/sources/${id}`)
      setSources(prev => prev.filter(s => s.id !== id))
      toast.success('Source deleted')
    } catch (e: any) { toast.error(e.message || 'Delete failed') }
  }

  const confirmDeleteFeedback = async () => {
    if (!pendingDeleteFeedback) return
    const id = pendingDeleteFeedback
    setPendingDeleteFeedback(null)
    setDeletingFeedback(id)
    try {
      await apiDelete(`/api/feedback/${id}`)
      setRows(prev => prev.filter(r => r.id !== id))
      toast.success('Feedback deleted')
    } catch (e: any) { toast.error(e.message || 'Delete failed') }
    finally { setDeletingFeedback(null) }
  }

  const load = async (rating?: 'up' | 'down') => {
    setLoading(true)
    try {
      const path = rating ? `/api/feedback/?rating=${rating}&limit=100` : '/api/feedback/?limit=100'
      setRows(await apiGet(path))
    } catch (e: any) { toast.error(e.message || 'Failed to load feedback') }
    finally { setLoading(false) }
  }

  useEffect(() => { load(filter === 'all' ? undefined : filter) }, [filter])

  useEffect(() => {
    if (adminTab !== 'feedback') return
    apiGet('/api/auth/users')
      .then((users: AdminUserSummary[]) => {
        setFeedbackUserMap(Object.fromEntries(users.map(u => [u.id, u])))
      })
      .catch(() => {
        // Feedback still works without this enrichment; hide submitter names if unavailable.
        setFeedbackUserMap({})
      })
  }, [adminTab])

  const startInject = (row: FeedbackRow) => {
    const lines: string[] = []
    if (row.rating === 'up') {
      lines.push('# Verified correct example — add context or label below (optional)')
      lines.push('')
      lines.push('# Agent response (will be injected as-is if unchanged):')
      lines.push(row.message_content?.slice(0, 1500) ?? '(no message content)')
    } else {
      if (row.comment) { lines.push('# User correction hint:'); lines.push(row.comment); lines.push('') }
      lines.push('CORRECT (what the agent should generate):')
      lines.push('[Replace this with the correct JSON / rule / value]')
      lines.push('')
      lines.push('WRONG (what the agent must NOT generate):')
      lines.push('[Replace this with the incorrect pattern]')
      if (row.message_content) {
        lines.push(''); lines.push('---')
        lines.push('# Agent response (reference — remove before injecting):')
        lines.push(row.message_content.slice(0, 1000))
      }
    }
    setCorrectionText(lines.join('\n'))
    setEditingId(row.id)
  }

  const confirmInject = async (feedbackId: string) => {
    setInjecting(feedbackId)
    try {
      const result = await apiPost(`/api/feedback/${feedbackId}/inject`, { corrected_text: correctionText.trim() })
      if (result.already_injected) toast.success('Already injected into RAG')
      else toast.success(`Injected — ${result.chunks_stored} chunk(s) added to RAG`)
      setRows(prev => prev.map(r => r.id === feedbackId ? { ...r, injected_to_rag: true } : r))
    } catch (e: any) { toast.error(e.message || 'Injection failed') }
    finally { setInjecting(null); setEditingId(null); setCorrectionText('') }
  }

  const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })

  return (
    <div className="admin-page">
      <div className="admin-header">
        <Button variant="ghost" size="sm" onClick={() => navigate('/projects')} style={{ marginBottom: 16, paddingLeft: 0 }}>
          <ArrowLeft size={13} /> Back to projects
        </Button>
        <h1>Admin Panel</h1>
        <p>Manage the knowledge base and review user feedback.</p>
      </div>

      {/* Tab switcher */}
      <div className="admin-filters" style={{ marginBottom: 20 }}>
        <button className={`filter-tab ${adminTab === 'feedback' ? 'active' : ''}`} onClick={() => setAdminTab('feedback')}>
          <ThumbsDown size={11} /> Feedback
        </button>
        <button className={`filter-tab ${adminTab === 'knowledge' ? 'active' : ''}`} onClick={() => setAdminTab('knowledge')}>
          <FileText size={11} /> Knowledge Base
        </button>
        <button className={`filter-tab ${adminTab === 'users' ? 'active' : ''}`} onClick={() => setAdminTab('users')}>
          <UserCheck size={11} /> Users
        </button>
        <button className={`filter-tab ${adminTab === 'usage' ? 'active' : ''}`} onClick={() => setAdminTab('usage')}>
          <Zap size={11} /> Usage
        </button>
      </div>

      {/* ── Knowledge Base Tab ──────────────────────────────────────────────── */}
      {adminTab === 'knowledge' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            <select
              value={selectedSourceType}
              onChange={e => setSelectedSourceType(e.target.value)}
              style={{ fontSize: 12.5, padding: '6px 10px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-raised)', color: 'var(--text-primary)', cursor: 'pointer' }}
            >
              {Object.entries(SOURCE_TYPE_LABELS).map(([v, label]) => (
                <option key={v} value={v}>{label}</option>
              ))}
            </select>
            <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
              <Upload size={12} /> {uploading ? 'Uploading...' : 'Upload file'}
            </Button>
            <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>JSON (OpenAPI/Postman), YAML, TXT, MD</span>
            <input ref={fileInputRef} type="file" accept=".json,.yaml,.yml,.txt,.md"
              onChange={handleFileUpload} style={{ display: 'none' }} />
          </div>

          {sourcesLoading ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading...</p>
          ) : sources.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No knowledge sources ingested yet.</p>
          ) : (
            <div className="admin-list">
              {sources.map(src => (
                <div key={src.id} style={{ padding: '12px 16px', background: 'var(--bg-raised)', border: '1px solid var(--border)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
                  <FileText size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {src.file_name || 'Unnamed source'}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 3, display: 'flex', gap: 10 }}>
                      <span>{SOURCE_TYPE_LABELS[src.source_type] || src.source_type}</span>
                      <span>·</span>
                      <span>{src.chunk_count ?? '?'} chunks</span>
                      <span>·</span>
                      <span>{new Date(src.ingested_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteSource(src.id, src.file_name || 'this source')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px 6px', borderRadius: 5, display: 'flex', alignItems: 'center' }}
                    title="Delete source" aria-label={`Delete ${src.file_name || 'knowledge source'}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Feedback Tab ────────────────────────────────────────────────────── */}
      {adminTab === 'feedback' && <>
      <div className="admin-filters">
        {(['all', 'down', 'up'] as const).map(f => (
          <button key={f} className={`filter-tab ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>
            {f === 'all' && 'All feedback'}
            {f === 'down' && <><ThumbsDown size={11} /> Thumbs down</>}
            {f === 'up' && <><ThumbsUp size={11} /> Thumbs up</>}
          </button>
        ))}
        <Badge variant="secondary" style={{ marginLeft: 'auto' }}>
          {rows.length} record{rows.length !== 1 ? 's' : ''}
        </Badge>
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13, padding: '32px 0' }}>Loading...</p>
      ) : rows.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13, padding: '32px 0' }}>No feedback yet.</p>
      ) : (
        <div className="admin-list">
          {rows.map(row => (
            <motion.div key={row.id} className={`admin-feedback-card ${row.rating}`}
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
              <div className="admin-card-header">
                <Badge variant={row.rating === 'up' ? 'accent' : 'destructive'}>
                  {row.rating === 'up' ? <><ThumbsUp size={9} /> Good</> : <><ThumbsDown size={9} /> Needs improvement</>}
                </Badge>
                {row.injected_to_rag && <Badge variant="info"><CheckCircle2 size={9} /> In RAG</Badge>}
                <span className="admin-date">{fmtDate(row.created_at)}</span>
                {row.user_id && (
                  <span className="admin-date">
                    Submitted by {feedbackUserMap[row.user_id]?.display_name || row.user_id.slice(0, 8)}
                  </span>
                )}
                {!row.injected_to_rag && (
                  <button
                    className="btn-icon-ghost"
                    title="Delete feedback"
                    aria-label="Delete feedback entry"
                    disabled={deletingFeedback === row.id}
                    onClick={() => setPendingDeleteFeedback(row.id)}
                    style={{ marginLeft: 'auto', color: 'var(--text-muted)', opacity: 0.6 }}
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
              {row.message_content && (
                <div className="admin-message-preview">
                  <span className="admin-label">Agent response</span>
                  <p>{row.message_content.slice(0, 400)}{row.message_content.length > 400 ? '…' : ''}</p>
                </div>
              )}
              {row.comment && (
                <div className="admin-comment">
                  <span className="admin-label">User comment</span>
                  <p>{row.comment}</p>
                </div>
              )}
              {editingId === row.id ? (
                <div className="admin-inject-form">
                  <span className="admin-label">
                    {row.rating === 'up' ? 'Add verified example to RAG' : 'Teach the agent — what should it do instead?'}
                  </span>
                  {row.rating === 'down' && (
                    <p className="admin-inject-hint">
                      Edit the template so it contains <strong>only the correct pattern</strong>.
                      Remove the reference section before injecting.
                    </p>
                  )}
                  <Textarea value={correctionText} onChange={e => setCorrectionText(e.target.value)}
                    rows={10} autoFocus style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}
                    placeholder={row.rating === 'up' ? 'Edit or annotate the example before injecting...' : 'Edit the correction before injecting...'} />
                  <div className="admin-inject-actions">
                    <Button variant="ghost" size="sm" onClick={() => { setEditingId(null); setCorrectionText('') }}>Cancel</Button>
                    <Button size="sm" onClick={() => confirmInject(row.id)} disabled={injecting === row.id || !correctionText.trim()}>
                      {injecting === row.id ? 'Injecting...' : <><Zap size={11} /> {row.rating === 'up' ? 'Add to RAG' : 'Inject into RAG'}</>}
                    </Button>
                  </div>
                </div>
              ) : row.injected_to_rag ? (
                <div style={{ marginTop: 8 }}>
                  <button className="btn-add-chunk" onClick={() => startInject(row)}>
                    <Plus size={11} /> {row.rating === 'up' ? 'Add another example' : 'Add another chunk'}
                  </button>
                </div>
              ) : (
                <div style={{ marginTop: 8 }}>
                  <button className="btn-inject" onClick={() => startInject(row)}>
                    {row.rating === 'up'
                      ? <><Zap size={11} /> Add to RAG</>
                      : <><ShieldAlert size={11} /> Review &amp; inject into RAG</>
                    }
                  </button>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!pendingDeleteFeedback}
        title="Delete feedback?"
        message="This feedback record will be permanently removed. This cannot be undone."
        confirmLabel="Delete"
        onConfirm={confirmDeleteFeedback}
        onCancel={() => setPendingDeleteFeedback(null)}
      />
      </>}

      {/* ── Users Tab ───────────────────────────────────────────────────────── */}
      {adminTab === 'users' && <>
        <div className="admin-filters">
          {(['pending', 'all'] as const).map(f => (
            <button key={f} className={`filter-tab ${userFilter === f ? 'active' : ''}`} onClick={() => setUserFilter(f)}>
              {f === 'pending' ? 'Pending approval' : 'All users'}
            </button>
          ))}
          <Badge variant="secondary" style={{ marginLeft: 'auto' }}>
            {userRows.length} user{userRows.length !== 1 ? 's' : ''}
          </Badge>
        </div>

        {usersLoading ? (
          <p style={{ color: 'var(--text-muted)', fontSize: 13, padding: '32px 0' }}>Loading...</p>
        ) : userRows.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: 13, padding: '32px 0' }}>
            {userFilter === 'pending' ? 'No users pending approval.' : 'No users found.'}
          </p>
        ) : (
          <div className="admin-list">
            {userRows.map(u => (
              <motion.div key={u.id} className="admin-feedback-card"
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
                <div className="admin-card-header">
                  <Badge variant={u.status === 'approved' ? 'accent' : u.status === 'suspended' ? 'destructive' : 'secondary'}>
                    {u.status === 'approved' ? 'Approved' : u.status === 'suspended' ? 'Suspended' : 'Pending'}
                  </Badge>
                  <Badge variant="secondary">{u.role}</Badge>
                  <span className="admin-date">{new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 500, marginTop: 6 }}>
                  {u.display_name || '(no name)'}
                </div>
                <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 3, overflowWrap: 'anywhere' }}>
                  {u.email || '(email unavailable)'}
                </div>
                <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                  {u.status === 'pending' && (
                    <>
                      <Button size="sm" onClick={() => updateUserStatus(u.id, 'approved')} disabled={updatingUserId === u.id}>
                        <UserCheck size={11} /> {updatingUserId === u.id ? 'Approving...' : 'Approve'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => updateUserStatus(u.id, 'suspended')} disabled={updatingUserId === u.id} style={{ color: 'var(--destructive)' }}>
                        {updatingUserId === u.id ? 'Rejecting...' : 'Reject'}
                      </Button>
                    </>
                  )}
                  {u.status === 'approved' && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => sendPasswordReset(u.id)} disabled={sendingResetId === u.id} title="Send a password reset email to this user">
                        <KeyRound size={11} /> {sendingResetId === u.id ? 'Sending…' : 'Send reset'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => updateUserStatus(u.id, 'suspended')} disabled={updatingUserId === u.id}>
                        {updatingUserId === u.id ? 'Suspending...' : 'Suspend'}
                      </Button>
                    </>
                  )}
                  {u.status === 'suspended' && (
                    <Button size="sm" variant="ghost" onClick={() => updateUserStatus(u.id, 'pending')} disabled={updatingUserId === u.id}>
                      {updatingUserId === u.id ? 'Resetting...' : 'Set to pending'}
                    </Button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </>}

      {/* ── Usage Tab ───────────────────────────────────────────────────────── */}
      {adminTab === 'usage' && <UsageDashboard />}

      <ConfirmDialog
        open={!!pendingDeleteSource}
        title="Delete knowledge source"
        message={pendingDeleteSource ? `"${pendingDeleteSource.name}" and all its chunks will be permanently deleted.` : ''}
        onConfirm={confirmDeleteSource}
        onCancel={() => setPendingDeleteSource(null)}
      />
    </div>
  )
}

// ─── Guards ───────────────────────────────────────────────────────────────────

function PendingApprovalPage({ suspended = false }: { suspended?: boolean }) {
  const { clearAuth } = useStore()
  const navigate = useNavigate()
  const handleLogout = async () => {
    try { await apiPost('/api/auth/logout', {}) } catch { /* ignore */ }
    clearAuth()
    navigate('/login')
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 16, padding: 32, textAlign: 'center' }}>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)' }}>
        {suspended ? 'Account suspended' : 'Awaiting approval'}
      </h2>
      <p style={{ color: 'var(--text-muted)', maxWidth: 380, fontSize: 14, lineHeight: 1.6 }}>
        {suspended
          ? 'Your account has been suspended. Contact your administrator for assistance.'
          : "Your account is pending admin approval. You'll receive access once an admin reviews your request."}
      </p>
      <Button variant="ghost" size="sm" onClick={handleLogout}>Log out</Button>
    </div>
  )
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, setAuth, clearAuth } = useStore()
  const [checking, setChecking] = useState(!user)
  useEffect(() => {
    if (user) { setChecking(false); return }
    bootstrapAuth()
      .then(u => {
        if (u) setAuth({ id: u.id, email: u.email, org_id: u.org_id, role: u.role as 'admin' | 'member' ?? 'member', status: u.status as 'pending' | 'approved' | 'suspended' ?? 'pending' })
        else clearAuth()
      })
      .finally(() => setChecking(false))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  if (checking) return null
  if (!user) return <Navigate to="/login" replace />
  if (user.status === 'pending') return <PendingApprovalPage />
  if (user.status === 'suspended') return <PendingApprovalPage suspended />
  return <>{children}</>
}

const SUPER_ADMIN_EMAIL = process.env.REACT_APP_SUPER_ADMIN_EMAIL || ''

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { user } = useStore()
  if (!user) return <Navigate to="/login" replace />
  const emailOk = !SUPER_ADMIN_EMAIL || user.email === SUPER_ADMIN_EMAIL
  if (user.role !== 'admin' || !emailOk) return <Navigate to="/projects" replace />
  return <>{children}</>
}

function SessionGuard() {
  const { clearAuth } = useStore()
  useEffect(() => {
    const handleExpired = () => {
      toast.error('Your session has expired. Logging you out…', { duration: 3000 })
      setTimeout(() => clearAuth(), 2500)
    }
    window.addEventListener('auth:session-expired', handleExpired)
    return () => window.removeEventListener('auth:session-expired', handleExpired)
  }, [clearAuth])
  return null
}

// ─── Root ─────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <BrowserRouter>
      <SessionGuard />
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: 'var(--bg-raised)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-active)',
            fontSize: 13,
            borderRadius: 10,
          }
        }}
      />
      <Routes>
        <Route path="/login" element={<AuthPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/projects" element={<RequireAuth><ProjectsPage /></RequireAuth>} />
        <Route path="/admin" element={<RequireAuth><RequireAdmin><AdminPage /></RequireAdmin></RequireAuth>} />
        <Route path="/project/:projectId/chat/:conversationId" element={<RequireAuth><Workspace /></RequireAuth>} />
        <Route path="/project/:projectId" element={<RequireAuth><Workspace /></RequireAuth>} />
        <Route path="*" element={<Navigate to="/projects" replace />} />
      </Routes>
    </BrowserRouter>
  )
}
